import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface BindSuccessState {
  company?: string;
  premium?: string;
  effectiveDate?: string;
  expirationDate?: string;
}

const CheckIcon = () => (
  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const SmallCheck = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const CopyIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);

const HomeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

const ArrowLeft = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"/>
    <polyline points="12 19 5 12 12 5"/>
  </svg>
);

interface BindSuccessProps extends Partial<BindSuccessState> {
  onBack?: () => void;
}

const BindSuccessPage: React.FC<BindSuccessProps> = (props) => {
  const navigate = useNavigate();
  const { state } = useLocation() as { state: BindSuccessState | null };

  const company        = props.company        ?? state?.company        ?? '—';
  const premium        = props.premium        ?? state?.premium        ?? '—';
  const effectiveDate  = props.effectiveDate  ?? state?.effectiveDate  ?? '—';
  const expirationDate = props.expirationDate ?? state?.expirationDate ?? '—';

  const refId = `MTM-${Date.now().toString(36).toUpperCase().slice(-8)}`;

  const handleCopyRef = () => {
    navigator.clipboard?.writeText(refId).catch(() => {});
  };

  return (
    <>
      <div className="bs">

        {/* ── Hero banner — same as dr-hero ── */}
        <div className="bs-hero">
          <div className="bs-hero__icon-wrap">
            <div className="bs-hero__ring" />
            <div className="bs-hero__circle">
              <CheckIcon />
            </div>
          </div>
          <div className="bs-hero__info">
            <h2 className="bs-hero__title">Uploaded to MTM Successfully!</h2>
            <div className="bs-hero__meta">
              <span className="bs-hero__id">{refId}</span>
              <span className="bs-hero__dot" />
              <span className="bs-hero__sub">{company}</span>
              <span className="bs-hero__dot" />
              <span className="bs-hero__sub">Premium: {premium}</span>
            </div>
          </div>
          <div className="bs-hero__actions">
            <span className="bs-status bs-status--complete">Submission Complete</span>
          </div>
        </div>

        {/* ── Two-column body — same as dr-body ── */}
        <div className="bs-body">

          {/* ── Left column ── */}
          <div className="bs-left">

            {/* Details grid */}
            <div className="bs-card">
              <div className="bs-card__header">
                <span className="bs-card__title-bar" />
                Submission Details
              </div>
              <div className="bs-details">
                <div className="bs-detail">
                  <span className="bs-detail__label">Insured</span>
                  <span className="bs-detail__value">{company}</span>
                </div>
                <div className="bs-detail">
                  <span className="bs-detail__label">Premium Amount</span>
                  <span className="bs-detail__value bs-detail__value--green">{premium}</span>
                </div>
                <div className="bs-detail">
                  <span className="bs-detail__label">Effective Date</span>
                  <span className="bs-detail__value">{effectiveDate}</span>
                </div>
                <div className="bs-detail">
                  <span className="bs-detail__label">Expiration Date</span>
                  <span className="bs-detail__value">{expirationDate}</span>
                </div>
              </div>
            </div>

            {/* Reference ID */}
            <div className="bs-card">
              <div className="bs-card__header">
                <span className="bs-card__title-bar" />
                Reference
              </div>
              <div className="bs-ref">
                <span className="bs-ref__label">Reference ID</span>
                <span className="bs-ref__value">{refId}</span>
                <button className="bs-ref__copy" onClick={handleCopyRef}>
                  <CopyIcon />
                  Copy
                </button>
              </div>
              <p className="bs-ref__note">
                Keep this reference ID to track your submission status with the underwriting team.
              </p>
            </div>

          </div>

          {/* ── Right column — same as dr-summary ── */}
          <div className="bs-summary">
            <div className="bs-summary__header">
              <p className="bs-summary__title">Submission Summary</p>
            </div>
            <div className="bs-summary__body">

              {/* Status indicator */}
              <div className="bs-summary-status">
                <div className="bs-summary-status__icon">
                  <CheckIcon />
                </div>
                <div>
                  <div className="bs-summary-status__label">Status</div>
                  <div className="bs-summary-status__value">Uploaded to MTM</div>
                </div>
              </div>

              {/* Checklist */}
              <div className="bs-summary-list">
                <div className="bs-summary-list__label">Next Steps</div>
                {[
                  'Documents sent to your email',
                  'Submission active in the market',
                  'Track progress from dashboard',
                ].map((text, i) => (
                  <div key={i} className="bs-summary-item">
                    <div className="bs-summary-item__icon bs-summary-item__icon--ok">
                      <SmallCheck />
                    </div>
                    <span className="bs-summary-item__label">{text}</span>
                  </div>
                ))}
              </div>

            </div>
          </div>

        </div>
      </div>

      {/* ── Footer — matches dr-footer exactly ── */}
      <div className="bs-footer">
        <button className="bs-footer__back" onClick={() => props.onBack ? props.onBack() : navigate(-1)}>
          <ArrowLeft />
          Back
        </button>
        <button className="bs-footer__action" onClick={() => navigate('/')}>
          <HomeIcon />
          Return to Dashboard
        </button>
      </div>
    </>
  );
};

export default BindSuccessPage;
